import React, { FC, useState } from 'react';
import { TaskId } from 'api/models';
import { Task as TaskApi } from 'api/tasks';
import { PageLoading } from 'components/atoms/loading';
import { PersonalTasks } from 'components/organisms/personal-tasks';
import { useHomeStore } from 'data/home/hooks';
import { usePersonalTasksStore } from 'data/personal-tasks/hooks';
import { Task } from 'data/tasks';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useCsatQuery } from 'queries';
import { AssignedToValue, GroupByValue, ViewValue } from '../filter';
import { Bar } from '../filter-bar';
import { Skeleton } from '../loader';
import { Approvals } from './approvals';
import { Csat } from './csat';
import { OverviewEmptyState } from './empty-state';
import { FilterMenu } from './filter-menu';
import { Filter } from './get-render-data';
import { Layout } from './layout';
import { List } from './list';
import styles from './overview.module.css';

const DEFAULT_FILTER_STATE: Filter = {
  view: ViewValue.Incomplete,
  assignedTo: AssignedToValue.Me,
  groupBy: GroupByValue.Default,
};

export const Overview: FC = () => {
  const { data, isLoading } = useHomeStore();
  const personalTasksQuery = usePersonalTasksStore();
  const [filter, setFilter] = useState<Filter>(DEFAULT_FILTER_STATE);

  const clearFilter = () => {
    setFilter(DEFAULT_FILTER_STATE);
  };

  const navigateToTask = useNavigateToTask();

  const handleFilterChange = (selected) => {
    setFilter({ ...filter, ...selected });
  };
  const openTask = (task: Task | TaskApi) => {
    const _projectId =
      typeof task.project === 'number' ? task.project : task.project?.projectId;

    navigateToTask(TaskId(_projectId, task.taskId));
  };

  const { data: csat, isLoading: isCsatLoading, refetch } = useCsatQuery();

  const hasData =
    data.myTasks.length > 0 ||
    data.approvals.length > 0 ||
    (csat ?? []).length > 0;

  return isLoading ? (
    <PageLoading />
  ) : (
    <>
      <Bar primary={<FilterMenu onChange={handleFilterChange} />} />
      <Layout
        main={
          <Skeleton active loading={isLoading || isCsatLoading}>
            {hasData ? (
              <>
                {filter.groupBy === GroupByValue.Default && (
                  <>
                    <Csat data={csat} onSubmit={refetch} />
                    <Approvals
                      data={data.approvals}
                      onTaskApprovalClick={openTask}
                    />
                  </>
                )}
                <List
                  key="list-tasks"
                  data={data.myTasks}
                  filter={filter}
                  openTask={openTask}
                  clearFilter={clearFilter}
                />
              </>
            ) : (
              <OverviewEmptyState />
            )}
          </Skeleton>
        }
        right={
          <>
            <Skeleton
              title
              active
              className={styles.loading}
              loading={personalTasksQuery.loading}
            />
            {!personalTasksQuery.loading && (
              <PersonalTasks data={personalTasksQuery.data} />
            )}
          </>
        }
      />
    </>
  );
};
